import BaseTable from "@/components/BaseTable/BaseTable";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import {
  customerSlaRecordToTableRow,
  normalizeCustomerSlaConfigurationList,
} from "@/lib/buildCustomerSlaConfigurationPayload";
import { getTenantId } from "@/lib/storeManager";
import SLAConfigurationService from "@/services/APIs/SLAConfigurationService";
import { gridName } from "@/utils/constant/gridName";
import { useA1ResolvedNav } from "@/hooks/useA1ResolvedNav";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { BookOpen, Edit2, Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import "./slaSchemaConfigurationListPage.css";

const SLA_CONNECTION_GUIDE_PDF = "/docs/SLA_JSON_Schema_final_Guide.pdf";

interface SLASchemaConfigRow {
  id: string;
  sla_policy_schema?: string;
  business_hours_schema?: string;
  holiday_calendar_schema?: string;
  escalation_time_schema?: string;
  notify_role_foreign_key_schema?: string;
}

const readLocalRows = (tenantId: string): SLASchemaConfigRow[] => {
  try {
    const raw = localStorage.getItem(`slaSchemaConfiguration:${tenantId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [{ id: tenantId, ...parsed }];
  } catch {
    return [];
  }
};

const SLASchemaConfigurationListPage = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const nav = useA1ResolvedNav();
  const queryClient = useQueryClient();

  const { data: apiList, isLoading, refetch } = useQuery({
    queryKey: ["customer-sla-configurations"],
    queryFn: () => SLAConfigurationService.listCustomerSlaConfigurations(),
  });

  const dataSource: SLASchemaConfigRow[] = useMemo(() => {
    const fromApi = normalizeCustomerSlaConfigurationList(apiList)
      .map(customerSlaRecordToTableRow)
      .filter(r => r.id);

    if (fromApi.length > 0) {
      return fromApi;
    }

    const tenantId = getTenantId();
    if (!tenantId) return [];
    return readLocalRows(tenantId);
  }, [apiList]);

  const handleDelete = async (id: string) => {
    try {
      await SLAConfigurationService.deleteCustomerSlaConfiguration(id);
      // Also clean up local storage if entry exists
      const tenantId = getTenantId();
      if (tenantId) {
        const raw = localStorage.getItem(`slaSchemaConfiguration:${tenantId}`);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const arr: SLASchemaConfigRow[] = Array.isArray(parsed)
              ? parsed
              : [{ id: tenantId, ...parsed }];
            const updated = arr.filter(r => r.id !== id);
            localStorage.setItem(
              `slaSchemaConfiguration:${tenantId}`,
              JSON.stringify(updated)
            );
          } catch {
            /* ignore */
          }
        }
      }
      notify.success("SLA configuration deleted.");
      void queryClient.invalidateQueries({ queryKey: ["customer-sla-configurations"] });
      void refetch();
    } catch {
      notify.error("Failed to delete SLA configuration. Please try again.");
    }
  };

  const openEdit = (row: SLASchemaConfigRow) => {
    navigate(`${nav.SLA_CONFIGURATION_ADD}?id=${encodeURIComponent(row.id)}`);
  };

  const openSlaConnectionGuide = () => {
    window.open(SLA_CONNECTION_GUIDE_PDF, "_blank", "noopener,noreferrer");
  };

  const columns: ProColumns<SLASchemaConfigRow>[] = [
    {
      title: "SLA Policy Schema",
      dataIndex: "sla_policy_schema",
      render: val => val ?? "—",
    },
    {
      title: "Business Hours Schema",
      dataIndex: "business_hours_schema",
      render: val => val ?? "—",
    },
    {
      title: "Holiday Calendar Schema",
      dataIndex: "holiday_calendar_schema",
      render: val => val ?? "—",
    },
    {
      title: "Escalation Time Schema",
      dataIndex: "escalation_time_schema",
      render: val => val ?? "—",
    },
    {
      title: "Main Apply Schema",
      dataIndex: "notify_role_foreign_key_schema",
      render: val => val ?? "—",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, row) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit"
          className="!p-1"
          onClick={() => openEdit(row)}
        />,
        <Popconfirm
          key="delete"
          title="Delete SLA Configuration"
          description="Are you sure you want to delete this SLA configuration?"
          onConfirm={() => handleDelete(row.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <CustomButton
            icon={<Trash2 size={16} />}
            type="text"
            danger
            title="Delete"
            className="!p-1"
          />
        </Popconfirm>,
      ],
    },
  ];

  const toolBarRender = () => [
    <CustomButton
      key="sla-guide"
      icon={<BookOpen size={16} />}
      onClick={openSlaConnectionGuide}
      title="Open SLA JSON schema connection guide (PDF)"
      className="sla-config-guide"
    >
      SLA Guide
    </CustomButton>,
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => navigate(nav.SLA_CONFIGURATION_ADD)}
      title="New SLA Configuration"
      className="sla-config-add"
    >
      New SLA
    </CustomButton>,
  ];

  return (
    <div className="sla-schema-config-list-page min-w-0">
      <BaseTable<SLASchemaConfigRow>
        loading={isLoading}
        columns={columns}
        dataSource={dataSource}
        scroll={isMobile ? { x: 960 } : {}}
        options={isMobile ? false : true}
        page={1}
        pageSize={10}
        total={dataSource.length}
        rowKey="id"
        toolBarRender={toolBarRender}
        headerTitle="SLA Schema Configurations"
        tableName={gridName.slaConfigurationTable}
        request={async () => {}}
        onPageChange={() => {}}
      />
    </div>
  );
};

export default SLASchemaConfigurationListPage;
